(() => {
  const PDP_SELECTOR = 'product-info.product-layout-figma';
  const NOTIFY_SELECTOR = '[data-notify-toggle]';
  const CARD_NOTIFY_SELECTOR = '[data-notify-card]';
  const NOTIFY_VARIANTS_SELECTOR = '[data-notify-variants]';
  const VARIANT_INPUT_SELECTOR = 'form[action^="/cart/add"] [name="id"]';
  const RETRY_DELAYS = [0, 150, 350, 700, 1200];
  const POPUP_SELECTOR = 'bis-popup';
  const DEFAULT_POPUP_TEXT = {
    title: 'We will notify you when this substance is restocked.',
    emailPlaceholder: 'Email address',
    buttonLabel: 'Notify me when available',
    success: "You're subscribed. We'll notify you when this variant is available again.",
    selectedVariantLabel: 'Selected variant',
    privacy: "We respect your privacy and do not share your email with anybody.",
    productFallback: 'Product',
  };

  const decodeHtml = (value) => {
    const string = String(value || '');
    if (!string.includes('&')) return string;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = string;
    return textarea.value;
  };

  const normalizeText = (value) => decodeHtml(value).replace(/\s+/g, ' ').trim();
  const normalizeId = (value) => String(value || '').trim();

  const getBisWidget = () => window.$bisWidget;
  const getNotifyVariantData = (scope = document) => {
    const script = scope?.querySelector?.(NOTIFY_VARIANTS_SELECTOR) || document.querySelector(NOTIFY_VARIANTS_SELECTOR);
    if (!script?.textContent) return [];

    try {
      const variants = JSON.parse(script.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      return [];
    }
  };

  const variantMatchesCandidate = (variant, candidate = {}) => {
    const candidateId = normalizeId(candidate.id || candidate.variantId);
    if (candidateId && normalizeId(variant?.id) === candidateId) return true;

    const candidateTexts = [candidate.text, candidate.value].map(normalizeText).filter(Boolean);
    if (!candidateTexts.length) return false;

    const variantTitle = normalizeText(variant?.title);
    const variantOptions = Array.isArray(variant?.options) ? variant.options.map(normalizeText) : [];

    return candidateTexts.some((text) => text === variantTitle || variantOptions.includes(text));
  };

  const getThemeVariant = (scope, candidate) =>
    getNotifyVariantData(scope).find((variant) => variantMatchesCandidate(variant, candidate));

  const variantIsTrackedAsUnavailable = (widget, variantId) => {
    const variants = widget?.product?.variants;
    if (!variants || typeof variants !== 'object') return false;
    return Object.prototype.hasOwnProperty.call(variants, String(variantId || ''));
  };

  const variantIsUnavailable = (scope, candidate = {}) => {
    const widget = getBisWidget();
    const variantId = normalizeId(candidate.id || candidate.variantId || candidate.value);
    if (variantId && variantIsTrackedAsUnavailable(widget, variantId)) return true;

    const themeVariant = getThemeVariant(scope, candidate);
    if (themeVariant) return themeVariant.available === false;

    return false;
  };

  const getPopupText = () => {
    const custom = window.perfumiarzNotifyPopup || {};

    return {
      title: decodeHtml(custom.title || DEFAULT_POPUP_TEXT.title),
      emailPlaceholder: decodeHtml(custom.emailPlaceholder || custom.email_placeholder || DEFAULT_POPUP_TEXT.emailPlaceholder),
      buttonLabel: decodeHtml(custom.buttonLabel || custom.button_label || DEFAULT_POPUP_TEXT.buttonLabel),
      success: decodeHtml(custom.success || DEFAULT_POPUP_TEXT.success),
      selectedVariantLabel: decodeHtml(
        custom.selectedVariantLabel || custom.selected_variant_label || DEFAULT_POPUP_TEXT.selectedVariantLabel
      ),
      privacy: decodeHtml(custom.privacy || DEFAULT_POPUP_TEXT.privacy),
      productFallback: decodeHtml(custom.productFallback || custom.product_fallback || DEFAULT_POPUP_TEXT.productFallback),
    };
  };

  const localizeBisWidget = (widget) => {
    if (!widget) return;

    const text = getPopupText();
    widget.text = text.buttonLabel;
    widget.customText = text.buttonLabel;
    widget.email_placeholder = text.emailPlaceholder;
    widget.privacy = text.privacy;
  };

  const getVariantId = (scope) => {
    const formVariantId = scope?.querySelector(VARIANT_INPUT_SELECTOR)?.value;
    if (formVariantId) return String(formVariantId);

    const widgetVariantId = getBisWidget()?.product?.variant;
    return widgetVariantId ? String(widgetVariantId) : '';
  };

  window.perfumiarzNotifyVariantState = {
    getVariantData: getNotifyVariantData,
    getVariantId,
    getThemeVariant,
    variantIsUnavailable,
  };

  const shouldEnableNotify = (scope) => {
    const variantId = getVariantId(scope);
    if (variantIsUnavailable(scope, { id: variantId })) return true;

    const widget = getBisWidget();
    if (!widget?.product?.bis) return false;

    if (widget.any) return true;
    if (!variantId) return Boolean(widget.product.bis);

    return variantIsTrackedAsUnavailable(widget, variantId);
  };

  const setNotifyState = (button, enabled) => {
    button.disabled = !enabled;
    button.setAttribute('aria-disabled', String(!enabled));
    button.classList.toggle('is-enabled', enabled);
  };

  const openBisPopup = (scope, popupProductState = null) => {
    const widget = getBisWidget();
    if (!widget?.product) return false;

    localizeBisWidget(widget);

    const variantId = getVariantId(scope);
    if (variantId) widget.product.variant = String(variantId);

    if (window.customElements?.get('bis-popup')) {
      const popup = document.createElement('bis-popup');
      if (typeof popup.init !== 'function') return false;
      popup._hnProductScope = scope;
      popup._hnNotifyProductState = popupProductState;
      popup.init(false);
      popup.classList.add('popup');
      document.body.appendChild(popup);
      window.requestAnimationFrame(() => enhanceBisPopup(popup, scope));
      return true;
    }

    const fallbackInline = scope?.querySelector('bis-inline') || document.querySelector('bis-inline');
    if (!fallbackInline) return false;
    fallbackInline.click();
    document.querySelectorAll(POPUP_SELECTOR).forEach((popup) => {
      popup._hnProductScope = scope;
      popup._hnNotifyProductState = popupProductState;
      enhanceBisPopup(popup, scope);
    });
    window.setTimeout(() => {
      document.querySelectorAll(POPUP_SELECTOR).forEach((popup) => {
        popup._hnProductScope = scope;
        popup._hnNotifyProductState = popupProductState;
        enhanceBisPopup(popup, scope);
      });
    }, 150);
    return true;
  };

  const getProductContext = (scope) => {
    const productScope = scope || document.querySelector(PDP_SELECTOR);
    const widgetProduct = getBisWidget()?.product;
    const cardTrigger = productScope?.matches?.(CARD_NOTIFY_SELECTOR)
      ? productScope
      : productScope?.querySelector?.(CARD_NOTIFY_SELECTOR);
    const title =
      cardTrigger?.dataset.productTitle ||
      productScope?.querySelector('.product__title h1')?.textContent?.trim() ||
      document.querySelector('.product__title h1')?.textContent?.trim() ||
      widgetProduct?.title ||
      getPopupText().productFallback;
    const image =
      productScope?.querySelector(
        '.perfumiarz-products__image, .related-products-figma__image, .perfumiarz-favorites__image'
      ) ||
      productScope?.querySelector('.product__media-wrapper img') ||
      document.querySelector('.product__media-wrapper img');

    return {
      title,
      imageSrc: image?.currentSrc || image?.src || widgetProduct?.image || '',
      imageAlt: image?.alt || title,
    };
  };

  const setBodyPopupState = () => {
    document.body.classList.toggle('hn-bis-open', Boolean(document.querySelector(`${POPUP_SELECTOR}.hn-bis-popup`)));
  };

  const closeBisPopup = (popup) => {
    const nativeClose = popup.querySelector(
      'button[class*="close" i]:not(.hn-bis-close), [aria-label*="close" i]:not(.hn-bis-close), [aria-label*="zamknij" i]:not(.hn-bis-close)'
    );

    if (nativeClose instanceof HTMLElement) {
      nativeClose.click();
    } else {
      popup.remove();
    }

    window.setTimeout(setBodyPopupState, 50);
  };

  const getPopupForm = (popup) =>
    popup.querySelector('input[type="email" i]')?.closest('form') || popup.querySelector('form');

  const getSubmitControl = (form) =>
    form?.querySelector('button[type="submit"], input[type="submit"], button:not([type]), button');

  const setSubmitControlLabel = (control, label) => {
    if (!control) return;

    if (control.tagName === 'INPUT') {
      if (control.value !== label) control.value = label;
      return;
    }

    const labelElement = control.querySelector('b, span') || control;
    if (normalizeText(labelElement.textContent) !== normalizeText(label)) {
      labelElement.textContent = label;
    }
  };

  const getEmailInput = (form) => form?.querySelector('input[type="email" i], input[name*="email" i]');

  const getVariantSelect = (form) => form?.querySelector('select');

  const syncVariantPills = (group, select) => {
    group.querySelectorAll('button').forEach((button) => {
      const active = button.dataset.value === select.value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  };

  const buildVariantPills = (select, scope) => {
    if (!select) return null;

    // Yanet creates this select only from unavailable variants. Reusing its
    // options also works on collection cards, which do not carry PDP variant JSON.
    const unavailableOptions = Array.from(select.options).filter((option) => option.value);

    if (!unavailableOptions.length) return null;

    const group = document.createElement('div');
    group.className = 'hn-bis-variants';

    unavailableOptions.forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hn-bis-variant';
      button.dataset.value = option.value;
      button.textContent = option.textContent.trim();
      button.disabled = false;
      button.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        if (window.$bisWidget?.product) {
          window.$bisWidget.product.variant = String(option.value);
        }
        syncVariantPills(group, select);
      });
      group.appendChild(button);
    });

    syncVariantPills(group, select);
    select.addEventListener('change', () => syncVariantPills(group, select));
    return group.childElementCount ? group : null;
  };

  const buildCardVariantPills = (popup, nativeSelect) => {
    const cardProduct = popup?._hnNotifyProductState;
    const variants = cardProduct?.variants ? Object.entries(cardProduct.variants) : [];
    if (!variants.length) return null;

    const group = document.createElement('div');
    group.className = 'hn-bis-variants';

    const syncActive = () => {
      const activeVariantId = normalizeId(getBisWidget()?.product?.variant);
      group.querySelectorAll('button').forEach((button) => {
        const active = normalizeId(button.dataset.value) === activeVariantId;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    variants.forEach(([variantId, variantTitle]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hn-bis-variant';
      button.dataset.value = normalizeId(variantId);
      button.textContent = normalizeText(variantTitle);
      button.addEventListener('click', () => {
        const widget = getBisWidget();
        if (widget?.product) widget.product.variant = normalizeId(variantId);

        if (nativeSelect) {
          nativeSelect.value = normalizeId(variantId);
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        syncActive();
      });
      group.appendChild(button);
    });

    syncActive();
    return group.childElementCount ? group : null;
  };

  const buildProductVariantPills = (scope, nativeSelect) => {
    const productScope = scope || document.querySelector(PDP_SELECTOR) || document;
    const variantRoot = productScope.querySelector('variant-selects') || document.querySelector('variant-selects');
    if (!variantRoot) return null;

    const optionInputs = Array.from(variantRoot.querySelectorAll('.product-form__input input[type="radio"]'));
    if (!optionInputs.length) return null;

    const group = document.createElement('div');
    group.className = 'hn-bis-variants';

    const syncActive = () => {
      group.querySelectorAll('button').forEach((button) => {
        const sourceInput = optionInputs.find((input) => input.value === button.dataset.optionValue);
        const active = Boolean(sourceInput?.checked);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    optionInputs.forEach((input) => {
      const label = input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
      const labelText = normalizeText(label?.textContent || input.value);
      if (!labelText) return;

      const matchedVariant = getThemeVariant(productScope, {
        value: input.value,
        text: labelText,
      });

      if (
        !variantIsUnavailable(productScope, {
          id: matchedVariant?.id,
          value: input.value,
          text: labelText,
        })
      ) {
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hn-bis-variant';
      button.dataset.variantId = normalizeId(matchedVariant?.id);
      button.dataset.optionValue = input.value;
      button.textContent = labelText;
      button.disabled = false;
      button.addEventListener('click', () => {
        if (!input.checked) input.click();

        window.setTimeout(() => {
          const variantId = normalizeId(matchedVariant?.id) || getVariantId(productScope);
          const widget = getBisWidget();
          if (variantId && widget?.product) widget.product.variant = String(variantId);

          if (nativeSelect) {
            const matchedOption = Array.from(nativeSelect.options).find(
              (option) => normalizeText(option.textContent) === labelText || option.value === input.value
            );
            if (matchedOption) {
              nativeSelect.value = matchedOption.value;
              nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }

          syncActive();
        }, 100);
      });

      group.appendChild(button);
    });

    syncActive();
    return group.childElementCount ? group : null;
  };

  const enhanceBisPopup = (popup, scope) => {
    if (!(popup instanceof Element) || popup.dataset.hnEnhanced === 'true') return;

    scope ||= popup._hnProductScope;

    const form = getPopupForm(popup);
    if (!form) return;

    const text = getPopupText();
    const product = getProductContext(scope);
    const emailInput = getEmailInput(form);
    const submitControl = getSubmitControl(form);
    const variantSelect = getVariantSelect(form);
    const card = form.parentElement || popup;
    const selectedVariantId = getVariantId(scope);
    if (variantSelect && selectedVariantId) {
      const selectedOption = Array.from(variantSelect.options).find(
        (option) => String(option.value) === String(selectedVariantId)
      );
      if (selectedOption) variantSelect.value = selectedOption.value;
    }
    const variantPills =
      buildCardVariantPills(popup, variantSelect) ||
      buildVariantPills(variantSelect, scope) ||
      buildProductVariantPills(scope, variantSelect);

    popup.dataset.hnEnhanced = 'true';
    popup.classList.add('hn-bis-popup');
    card.classList.add('hn-bis-card');
    form.classList.add('hn-bis-form');
    variantSelect?.classList.add('hn-bis-native-select');

    Array.from(card.children).forEach((child) => {
      if (child !== form) child.classList.add('hn-bis-hidden-original');
    });

    const shell = document.createElement('div');
    shell.className = 'hn-bis-shell hn-bis-design-shell';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'hn-bis-close';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.innerHTML = '<span aria-hidden="true"></span>';
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      closeBisPopup(popup);
    });

    const title = document.createElement('p');
    title.className = 'hn-bis-title';
    title.textContent = text.title;

    const productBlock = document.createElement('div');
    productBlock.className = 'hn-bis-product';
    if (product.imageSrc) {
      const image = document.createElement('img');
      image.className = 'hn-bis-image';
      image.src = product.imageSrc;
      image.alt = product.imageAlt;
      productBlock.appendChild(image);
    }

    const productTitle = document.createElement('h2');
    productTitle.className = 'hn-bis-product-title';
    productTitle.textContent = product.title;
    productBlock.appendChild(productTitle);

    shell.append(closeButton, title, productBlock);
    card.insertBefore(shell, form);

    if (variantSelect) {
      variantSelect.classList.add('hn-bis-native-select');
    }

    if (variantPills) {
      // Yanet nests its controls in form-group wrappers. Adding the custom
      // variant group as a direct child avoids an invalid insertBefore anchor.
      form.prepend(variantPills);
    }

    if (emailInput) {
      emailInput.placeholder = text.emailPlaceholder;
      emailInput.classList.add('hn-bis-email');
      emailInput.removeAttribute('readonly');
      emailInput.removeAttribute('disabled');
      emailInput.readOnly = false;
      emailInput.disabled = false;
      if (emailInput.parentElement && emailInput.parentElement !== form) {
        emailInput.parentElement.classList.add('hn-bis-email-row');
      }
    }

    if (submitControl) {
      setSubmitControlLabel(submitControl, text.buttonLabel);
      submitControl.classList.add('hn-bis-submit');
      if (submitControl.parentElement && submitControl.parentElement !== form) {
        submitControl.parentElement.classList.add('hn-bis-submit-row');
      }
    }

    Array.from(form.querySelectorAll('label')).forEach((label) => {
      if (!label.contains(emailInput) && !label.contains(submitControl)) {
        label.classList.add('hn-bis-hidden-original');
      }
    });

    Array.from(form.querySelectorAll('*')).forEach((element) => {
      if (
        element === form ||
        element === emailInput ||
        element === submitControl ||
        element === variantSelect ||
        element.classList.contains('hn-bis-variants') ||
        element.contains(emailInput) ||
        element.contains(submitControl) ||
        element.contains(variantSelect)
      ) {
        return;
      }

      if (normalizeText(element.textContent) === normalizeText(product.title)) {
        element.classList.add('hn-bis-hidden-original');
      }
    });

    Array.from(form.children).forEach((child) => {
      if (
        child === variantPills ||
        child.contains(emailInput) ||
        child.contains(submitControl) ||
        child.contains(variantSelect) ||
        child.tagName === 'SCRIPT' ||
        child.tagName === 'STYLE'
      ) {
        return;
      }

      const childText = child.textContent?.trim();
      if (childText && !child.querySelector('input, button, select, textarea')) {
        child.classList.add('hn-bis-hidden-original');
      }
    });

    const privacy = document.createElement('p');
    privacy.className = 'hn-bis-privacy';
    privacy.textContent = text.privacy;
    card.appendChild(privacy);

    document.body.classList.add('hn-bis-open');
  };

  const getCardProductJsonUrl = (button) => {
    const productUrl = button.dataset.productUrl;
    if (!productUrl) return '';

    const url = new URL(productUrl, window.location.origin);
    url.hash = '';
    url.search = '';
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return url.toString();
  };

  const setCardWidgetProduct = (button, product) => {
    const widget = getBisWidget();
    if (!widget || !product) return false;

    const productVariants = Array.isArray(product.variants) ? product.variants : [];
    const unavailableVariants = productVariants.filter((variant) => variant.available === false);
    if (!unavailableVariants.length) return false;

    const requestedVariantId = normalizeId(button.dataset.productVariantId);
    const selectedVariant =
      unavailableVariants.find((variant) => normalizeId(variant.id) === requestedVariantId) || unavailableVariants[0];
    const featuredImage =
      (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src) ||
      product.images?.[0] ||
      '';
    const productState = {
      bis: true,
      id: normalizeId(product.id || button.dataset.productId),
      title: button.dataset.productTitle || product.title || getPopupText().productFallback,
      image: featuredImage,
      variant: normalizeId(selectedVariant.id),
      selectedVariantTitle: normalizeText(selectedVariant.title),
    };

    if (productVariants.length > 1) {
      productState.variants = Object.fromEntries(
        productVariants.map((variant) => [normalizeId(variant.id), normalizeText(variant.title)])
      );
      productState.options = Array.isArray(product.options)
        ? product.options.map((option) => normalizeText(option?.name || option)).filter(Boolean)
        : [];
    }

    widget.products ||= {};
    const handle = button.dataset.productHandle || product.handle;
    if (handle) widget.products[handle] = productState;
    widget.product = productState;
    return productState;
  };

  const navigateToCardProduct = (button) => {
    const productUrl = button.dataset.productUrl;
    if (!productUrl) return;

    const url = new URL(productUrl, window.location.origin);
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    window.location.assign(url.toString());
  };

  const openCardBisPopup = async (button) => {
    if (button.dataset.notifyLoading === 'true') return;

    const productJsonUrl = getCardProductJsonUrl(button);
    if (!productJsonUrl) return;

    button.dataset.notifyLoading = 'true';
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(productJsonUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Unable to load product (${response.status})`);
      const product = await response.json();
      const scope = button.closest('article') || button;

      const tryOpen = (attempt = 0) => {
        const productState = setCardWidgetProduct(button, product);
        if (productState && openBisPopup(scope, productState)) {
          button.removeAttribute('aria-busy');
          delete button.dataset.notifyLoading;
          return;
        }

        if (attempt < RETRY_DELAYS.length - 1) {
          window.setTimeout(() => tryOpen(attempt + 1), RETRY_DELAYS[attempt + 1]);
          return;
        }

        button.removeAttribute('aria-busy');
        delete button.dataset.notifyLoading;
        navigateToCardProduct(button);
      };

      tryOpen();
    } catch (error) {
      button.removeAttribute('aria-busy');
      delete button.dataset.notifyLoading;
      navigateToCardProduct(button);
    }
  };

  const syncPopupSuccessState = (popup) => {
    if (!(popup instanceof Element) || popup.dataset.hnEnhanced !== 'true') return;
    if (popup.classList.contains('hn-bis-complete')) return;

    const nativeSuccess = popup.querySelector('h3.success, .success');
    if (!nativeSuccess || getPopupForm(popup)) return;

    const card = popup.querySelector('.hn-bis-card');
    if (!card) return;

    const widgetProduct = getBisWidget()?.product;
    const selectedVariantId = normalizeId(widgetProduct?.variant);
    const selectedVariant = normalizeText(
      widgetProduct?.variants?.[selectedVariantId] || widgetProduct?.selectedVariantTitle
    );
    const text = getPopupText();

    if (selectedVariant) {
      const variant = document.createElement('p');
      variant.className = 'hn-bis-selected-variant';
      variant.textContent = `${text.selectedVariantLabel}: ${selectedVariant}`;
      card.querySelector('.hn-bis-shell')?.appendChild(variant);
    }

    const message = document.createElement('p');
    message.className = 'hn-bis-success';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.textContent = text.success;

    popup.querySelectorAll('.hn-bis-variants').forEach((group) => {
      group.hidden = true;
      group.setAttribute('aria-hidden', 'true');
      group.querySelectorAll('button').forEach((button) => {
        button.disabled = true;
      });
    });

    const privacy = card.querySelector(':scope > .hn-bis-privacy');
    card.insertBefore(message, privacy || null);
    popup.classList.add('hn-bis-complete');
  };

  const syncNotifyButton = (scope) => {
    if (!(scope instanceof Element)) return;
    const button = scope.querySelector(NOTIFY_SELECTOR);
    if (!button) return;
    setNotifyState(button, shouldEnableNotify(scope));
  };

  const syncAllNotifyButtons = () => {
    document.querySelectorAll(PDP_SELECTOR).forEach((scope) => syncNotifyButton(scope));
  };

  const queueSync = () => {
    RETRY_DELAYS.forEach((delay) => {
      window.setTimeout(syncAllNotifyButtons, delay);
    });
  };

  const handleNotifyClick = (event) => {
    const cardButton = event.target.closest(CARD_NOTIFY_SELECTOR);
    if (cardButton) {
      event.preventDefault();
      event.stopPropagation();
      openCardBisPopup(cardButton);
      return;
    }

    const button = event.target.closest(NOTIFY_SELECTOR);
    if (!button) return;

    const scope = button.closest(PDP_SELECTOR);
    if (!scope || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const tryOpen = (attempt = 0) => {
      if (openBisPopup(scope)) return;
      if (attempt >= RETRY_DELAYS.length - 1) return;
      window.setTimeout(() => tryOpen(attempt + 1), RETRY_DELAYS[attempt + 1]);
    };

    tryOpen();
  };

  const bootstrap = () => {
    document.addEventListener('click', handleNotifyClick);
    document.addEventListener('product-info:loaded', queueSync);
    document.addEventListener('shopify:section:load', queueSync);
    document.addEventListener('theme:loading:end', queueSync);
    document.addEventListener('change', (event) => {
      if (event.target.matches?.(VARIANT_INPUT_SELECTOR) || event.target.closest?.('variant-selects')) queueSync();
    });

    new MutationObserver((mutations) => {
      const hasBisOrNotifyUpdate = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(`${PDP_SELECTOR}, bis-inline, bis-popup`) ||
              node.querySelector?.(`${PDP_SELECTOR}, bis-inline, bis-popup`))
        )
      );

      if (hasBisOrNotifyUpdate) {
        queueSync();
      }

      document.querySelectorAll(POPUP_SELECTOR).forEach((popup) => {
        if (hasBisOrNotifyUpdate) enhanceBisPopup(popup);
        syncPopupSuccessState(popup);
      });

      setBodyPopupState();
    }).observe(document.body, { childList: true, subtree: true });

    queueSync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();

(() => {
  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const popupSelector = 'bis-popup.hn-bis-popup, bis-popup';
  const pdpSelector = 'product-info.product-layout-figma';

  const getNotifyVariantState = () => window.perfumiarzNotifyVariantState;
  const getPdpScope = () => document.querySelector(pdpSelector) || document;

  const getProductTitle = () =>
    normalize(
      document.querySelector('main .product__title h1, main h1.product__title, main .product h1, main h1')?.textContent
    );

  const getActiveVariantId = () => {
    const input = document.querySelector('form[action^="/cart/add"] [name="id"]');
    return input?.value || String(window.$bisWidget?.product?.variant || '');
  };

  const getVariantData = () => {
    const variantState = getNotifyVariantState();
    const productScope = getPdpScope();
    const radios = Array.from(document.querySelectorAll('variant-selects input[type="radio"]'));

    if (radios.length) {
      return radios
        .map((input) => {
          const label = input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
          const labelClone = label?.cloneNode(true);
          labelClone?.querySelectorAll('.visually-hidden, .label-unavailable').forEach((node) => node.remove());
          const text = normalize(labelClone?.textContent || input.value)
            .replace(/\bVariant sold out or unavailable\b/gi, '')
            .trim();
          if (!text) return null;

          const matchedVariant = variantState?.getThemeVariant(productScope, {
            value: input.value,
            text,
          });
          const variant = {
            active: input.checked,
            disabled: false,
            id: String(input.getAttribute('data-variant-id') || matchedVariant?.id || ''),
            input,
            text,
            value: input.value,
          };

          if (variantState && !variantState.variantIsUnavailable(productScope, variant)) return null;

          return variant;
        })
        .filter(Boolean);
    }

    const themeVariants = variantState?.getVariantData(productScope) || [];
    if (themeVariants.length) {
      return themeVariants
        .filter((variant) => variant.available === false)
        .map((variant) => ({
          active: String(variant.id) === String(getActiveVariantId()),
          disabled: false,
          id: String(variant.id || ''),
          input: null,
          text: normalize(variant.title),
          value: String(variant.id || ''),
        }))
        .filter((variant) => variant.text);
    }

    const widgetVariants = window.$bisWidget?.product?.variants;
    if (widgetVariants && typeof widgetVariants === 'object') {
      const activeVariantId = getActiveVariantId();

      return Object.entries(widgetVariants)
        .map(([id, data]) => {
          const text = normalize(data?.title || data?.name || data?.option1 || data?.option || data?.variant_title || data?.variant || data?.label);
          if (!text || text === id) return null;

          return {
            active: String(id) === String(activeVariantId),
            disabled: false,
            id,
            input: null,
            text,
            value: id,
          };
        })
        .filter(Boolean);
    }

    return [];
  };

  const syncVariantButtons = (group) => {
    const activeVariantId = getActiveVariantId();

    group.querySelectorAll('.hn-bis-variant').forEach((button) => {
      const input = button._hnSourceInput;
      const active = input ? input.checked : button.dataset.variantId === activeVariantId;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  };

  const addVariantPicker = (popup) => {
    const form = popup.querySelector('form');
    if (!form || popup.querySelector('.hn-bis-variants')) return;

    const variants = getVariantData();
    if (variants.length < 2) return;

    const group = document.createElement('div');
    group.className = 'hn-bis-variants';

    variants.forEach((variant) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hn-bis-variant';
      button.textContent = variant.text;
      button.disabled = variant.disabled;
      button.dataset.variantId = variant.id || '';
      button.dataset.optionValue = variant.value || '';
      button._hnSourceInput = variant.input;

      button.addEventListener('click', () => {
        if (variant.input && !variant.input.checked) {
          variant.input.click();
        }

        window.setTimeout(() => {
          const currentVariantId = variant.id || getActiveVariantId();
          if (currentVariantId && window.$bisWidget?.product) {
            window.$bisWidget.product.variant = String(currentVariantId);
          }
          syncVariantButtons(group);
        }, 100);
      });

      group.appendChild(button);
    });

    const productBlock = popup.querySelector('.hn-bis-product');
    if (productBlock?.parentElement) {
      productBlock.insertAdjacentElement('afterend', group);
    } else {
      form.insertBefore(group, form.firstChild);
    }
    syncVariantButtons(group);
  };

  const hideDuplicateProductName = (popup) => {
    const productTitle = normalize(popup.querySelector('.hn-bis-product-title')?.textContent) || getProductTitle();
    if (!productTitle) return;

    popup.querySelectorAll('p, h1, h2, h3, h4, h5, h6, div, span, label, strong').forEach((element) => {
      if (element.matches('.hn-bis-title, .hn-bis-product-title')) return;
      if (element.closest('.hn-bis-product')) return;
      if (element.querySelector('input, button, select, textarea, img, svg')) return;
      if (normalize(element.textContent) !== productTitle) return;

      element.classList.add('hn-bis-hidden-original');
      element.setAttribute('aria-hidden', 'true');
    });
  };

  const styleNotifyControls = (popup) => {
    const form = popup.querySelector('form');
    if (!form) return;

    const popupText = window.perfumiarzNotifyPopup || {};
    const email = form.querySelector('.hn-bis-email, input[type="email"], input[name*="email" i]');
    const submit = form.querySelector('.hn-bis-submit, button[type="submit"], input[type="submit"]');

    if (email) {
      email.classList.add('hn-bis-email');
      email.placeholder = popupText.emailPlaceholder || popupText.email_placeholder || 'Email address';
    }

    if (submit) {
      submit.classList.add('hn-bis-submit');
      const buttonLabel = popupText.buttonLabel || popupText.button_label || 'Notify me when available';
      if (submit.tagName === 'INPUT') {
        if (submit.value !== buttonLabel) submit.value = buttonLabel;
      } else {
        const labelElement = submit.querySelector('b, span') || submit;
        if (normalize(labelElement.textContent) !== normalize(buttonLabel)) {
          labelElement.textContent = buttonLabel;
        }
      }
    }

    let privacy = popup.querySelector('.hn-bis-privacy');
    if (!privacy) {
      privacy = document.createElement('p');
      privacy.className = 'hn-bis-privacy';
      privacy.textContent =
        popupText.privacy || 'We respect your privacy and do not share your email with anybody.';
      form.insertAdjacentElement('afterend', privacy);
    }
  };

  const cleanPopup = (popup) => {
    if (!(popup instanceof Element)) return;

    hideDuplicateProductName(popup);
    addVariantPicker(popup);
    styleNotifyControls(popup);
  };

  const scan = () => document.querySelectorAll(popupSelector).forEach(cleanPopup);

  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('variant:change', scan);
  document.addEventListener('change', (event) => {
    if (event.target?.matches?.('variant-selects input, variant-selects select')) scan();
  });
  scan();
})();
